import React, { useState, useEffect } from 'react';
import vulnApi from '../../services/vulnApi';
import offensiveApi from '../../services/offensiveApi';

/**
 * Split however a scope was pasted — one per line, comma-separated straight out
 * of an engagement scope, or copied from a document.
 *
 * A comma-separated paste used to be stored as a single target, which nmap
 * cannot resolve. The scan then failed with "Agent returned no valid XML
 * output", which says nothing about the target list being the problem.
 */
const splitTargets = (text) =>
  (text || '').split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);

/** Union, order preserved — pasted scopes repeat ranges. */
const mergeTargets = (existing, incoming) => {
  const seen = new Set();
  const merged = [];
  for (const t of [...splitTargets(existing), ...incoming]) {
    if (!seen.has(t)) { seen.add(t); merged.push(t); }
  }
  return merged.join('\n');
};

/** Scope fields are JSON strings, and may themselves hold a comma-joined blob. */
const parseScopeField = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return splitTargets(Array.isArray(parsed) ? parsed.join(' ') : String(parsed));
  } catch {
    return splitTargets(raw);
  }
};

function CreateScanModal({ isOpen, onClose, onCreated, customerId: initialCustomerId }) {
  const [name, setName] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || '');
  const [scanType, setScanType] = useState('quick');
  const [externalTargets, setExternalTargets] = useState('');
  const [internalTargets, setInternalTargets] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agents, setAgents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [scopeEngagementId, setScopeEngagementId] = useState('');
  const [scopeNotice, setScopeNotice] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    vulnApi.getCustomers().then((data) => {
      const list = Array.isArray(data) ? data : [];
      setCustomers(list);
      if (!initialCustomerId && list.length > 0) {
        setSelectedCustomerId(list[0].id);
      }
    }).catch(() => {});
    vulnApi.getAgents().then((data) => {
      setAgents(Array.isArray(data) ? data : []);
    }).catch(() => setAgents([]));
    offensiveApi.getEngagements().then((data) => {
      setEngagements(Array.isArray(data) ? data : []);
    }).catch(() => setEngagements([]));
  }, [isOpen]);

  useEffect(() => {
    setSelectedCustomerId(initialCustomerId || '');
  }, [initialCustomerId]);

  // Only this customer's engagements, and only those that actually carry a
  // scope — a customer routinely has several with different agreed ranges, so
  // "the" scope does not exist and has to be chosen.
  const scopedEngagements = engagements.filter((e) => {
    if (String(e.customer?.id ?? e.customer_id) !== String(selectedCustomerId)) return false;
    const scope = e.offensive_scope;
    if (!scope) return false;
    return parseScopeField(scope.target_networks).length > 0
        || parseScopeField(scope.external_targets).length > 0;
  });

  const applyScope = () => {
    const engagement = scopedEngagements.find((e) => String(e.id) === String(scopeEngagementId));
    if (!engagement) return;
    const scope = engagement.offensive_scope || {};
    const internal = parseScopeField(scope.target_networks);
    const external = parseScopeField(scope.external_targets);

    // Merge rather than replace: the analyst may have typed something already,
    // and re-clicking should be idempotent rather than destructive.
    setInternalTargets((prev) => mergeTargets(prev, internal));
    setExternalTargets((prev) => mergeTargets(prev, external));
    setScopeNotice(
      `Added ${internal.length} internal and ${external.length} external ranges from ` +
      `${engagement.engagement_code || `engagement ${engagement.id}`}.`
    );
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const extList = splitTargets(externalTargets);
    const intList = splitTargets(internalTargets);

    if (extList.length === 0 && intList.length === 0) {
      setError('Enter at least one target');
      return;
    }

    if (intList.length > 0 && !agentId) {
      setError('Internal targets require an Agent ID');
      return;
    }

    if (!selectedCustomerId) {
      setError('Please select a customer');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        customer_id: selectedCustomerId,
        name: name || `Scan ${new Date().toLocaleString()}`,
        scan_type: scanType,
        external_targets: extList.length > 0 ? JSON.stringify(extList) : null,
        targets: intList.length > 0 ? JSON.stringify(intList) : null,
        agent_id: agentId ? parseInt(agentId, 10) : null,
      };

      const scan = await vulnApi.createScan(payload);
      onCreated(scan);
      onClose();
      setName('');
      setExternalTargets('');
      setInternalTargets('');
      setAgentId('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-70 z-50 flex items-center justify-center px-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">New Vulnerability Scan</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-500">
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 space-y-4">
            {error && (
              <div className="bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Customer
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(Number(e.target.value))}
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm"
              >
                <option value="">Select customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Scan Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Weekly perimeter scan"
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Scan Type
              </label>
              <select
                value={scanType}
                onChange={(e) => setScanType(e.target.value)}
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm"
              >
                <option value="quick">Quick</option>
                <option value="standard">Standard</option>
                <option value="full">Full</option>
                <option value="udp">UDP</option>
              </select>
            </div>

            {/* Targets */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Targets</h3>

              {scopedEngagements.length > 0 && (
                <div className="mb-3 p-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={scopeEngagementId}
                      onChange={(e) => { setScopeEngagementId(e.target.value); setScopeNotice(null); }}
                      className="flex-1 min-w-[12rem] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select an engagement…</option>
                      {scopedEngagements.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.engagement_code || `Engagement ${e.id}`}
                          {e.name ? ` — ${e.name}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={applyScope}
                      disabled={!scopeEngagementId}
                      className="px-3 py-1.5 rounded text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                    >
                      From scope items
                    </button>
                  </div>
                  {scopeNotice && (
                    <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{scopeNotice}</p>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  External Targets <span className="text-slate-400 font-normal">(public IPs/hostnames, one per line)</span>
                </label>
                <textarea
                  value={externalTargets}
                  onChange={(e) => setExternalTargets(e.target.value)}
                  placeholder={"8.8.8.8\nexample.com\n203.0.113.0/24"}
                  rows={3}
                  className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Internal Targets <span className="text-slate-400 font-normal">(private IPs/hostnames, one per line)</span>
                </label>
                <textarea
                  value={internalTargets}
                  onChange={(e) => setInternalTargets(e.target.value)}
                  placeholder={"192.168.1.0/24\n10.0.0.1\nintranet.local"}
                  rows={3}
                  className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {internalTargets.trim().length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Agent VM <span className="text-rose-400">*</span>
                  </label>
                  {agents.length > 0 ? (
                    <select
                      value={agentId}
                      onChange={(e) => setAgentId(e.target.value)}
                      className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="">Select agent...</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.agent_name} — {a.vpn_ip || 'no VPN IP'} ({a.status})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-amber-500 dark:text-amber-400">
                      No active agents available. Deploy an agent VM from Offensive &gt; Operations to scan internal targets.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300 px-4 py-2 rounded text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Scan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateScanModal;
